import { useCallback, useState } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';

/**
 * Deferred email verification (NEA-5773).
 *
 * The Unify Clerk app has "Verify at sign-up" OFF (so email+password sign-up
 * auto-logs-in — NEA-5767), which leaves new users signed in with an
 * *unverified* primary email. This hook drives the after-the-fact verification
 * against the signed-in user's `EmailAddress` resource (NOT the `signUp`
 * object, which is gone by the time the dashboard banner renders):
 *
 *   - `sendCode()`        → prepareVerification({ strategy: 'email_code' })
 *   - `verifyCode(code)`  → attemptVerification({ code }) then user.reload()
 *
 * `needsVerification` gates the dashboard banner + the "go live" action;
 * `user.reload()` after a successful attempt flips it so the banner clears.
 */

const friendlyError = (err) => {
	const code = err?.errors?.[0]?.code;
	switch (code) {
		case 'verification_expired':
		case 'verification_failed':
			return 'That code has expired. Send a new one and try again.';
		case 'form_code_incorrect':
			return 'That code is incorrect. Check it and try again.';
		case 'too_many_requests':
		case 'rate_limit_exceeded':
			return 'Too many attempts. Please wait a moment and try again.';
		default:
			return err?.errors?.[0]?.message || 'Something went wrong. Please try again.';
	}
};

export function useEmailVerification() {
	const { user, isLoaded, isSignedIn } = useUser();
	const { sessionClaims } = useAuth();
	// HQ staff impersonating a retailer ("view as") must not be gated or trigger
	// a verification email to the retailer — mirrors useEnsureLoopsContact, and
	// keeps "go live" / expansion reachable via impersonation (per add-store).
	const isImpersonating = Boolean(sessionClaims?.act);

	const [isSending, setIsSending] = useState(false);
	const [isVerifying, setIsVerifying] = useState(false);
	const [codeSent, setCodeSent] = useState(false);
	const [error, setError] = useState(null);

	const emailAddress = user?.primaryEmailAddress ?? null;
	const email = emailAddress?.emailAddress ?? null;
	const status = emailAddress?.verification?.status ?? null;
	// Only gate once we *know* the user is signed in with an unverified email —
	// an unloaded/signed-out state must not flash the banner.
	const needsVerification = Boolean(isLoaded && isSignedIn && !isImpersonating && emailAddress && status !== 'verified');

	const sendCode = useCallback(async () => {
		if (!emailAddress) return { success: false };
		setIsSending(true);
		setError(null);
		try {
			await emailAddress.prepareVerification({ strategy: 'email_code' });
			setCodeSent(true);
			return { success: true };
		} catch (err) {
			setError(friendlyError(err));
			return { success: false, error: friendlyError(err) };
		} finally {
			setIsSending(false);
		}
	}, [emailAddress]);

	const verifyCode = useCallback(
		async (code) => {
			if (!emailAddress) return { success: false };
			const trimmed = (code ?? '').trim();
			if (!trimmed) {
				setError('Enter the code we emailed you.');
				return { success: false };
			}
			setIsVerifying(true);
			setError(null);
			try {
				await emailAddress.attemptVerification({ code: trimmed });
				// Refresh the user so `primaryEmailAddress.verification.status`
				// flips to 'verified' and `needsVerification` clears app-wide.
				await user.reload();
				return { success: true };
			} catch (err) {
				setError(friendlyError(err));
				return { success: false, error: friendlyError(err) };
			} finally {
				setIsVerifying(false);
			}
		},
		[emailAddress, user]
	);

	const clearError = useCallback(() => setError(null), []);

	return {
		isLoaded,
		needsVerification,
		email,
		status,
		codeSent,
		isSending,
		isVerifying,
		error,
		sendCode,
		verifyCode,
		clearError
	};
}
